import { z } from 'zod';
import { patchOf } from './patch';
import { ASPECT_RATIOS, STORYBOARD_STATUSES } from '../domain';
import { fileDto } from './files';

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => v ?? '');
const optionalId = z
  .uuid()
  .nullable()
  .optional()
  .transform((v) => v ?? null);

export const storyboardInput = z.object({
  title: z.string().trim().min(1, 'Dê um nome ao storyboard').max(160),
  description: z
    .string()
    .trim()
    .max(4000)
    .optional()
    .transform((v) => v || null),
  projectId: optionalId,
  clientId: optionalId,
  taskId: optionalId,
  fps: z.number().int().min(1).max(120).default(24),
  aspectRatio: z.enum(ASPECT_RATIOS).default('16:9'),
  status: z.enum(STORYBOARD_STATUSES).default('draft'),
});
export type StoryboardInput = z.input<typeof storyboardInput>;
export const storyboardPatch = patchOf(storyboardInput);

export const storyboardDto = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  projectId: z.uuid().nullable(),
  projectName: z.string().nullable(),
  clientId: z.uuid().nullable(),
  clientName: z.string().nullable(),
  taskId: z.uuid().nullable(),
  fps: z.number(),
  aspectRatio: z.enum(ASPECT_RATIOS),
  status: z.enum(STORYBOARD_STATUSES),
  versionNumber: z.number(),
  sceneCount: z.number(),
  durationMs: z.number(),
  /** Até 4 quadros, para o card da lista. */
  preview: z.array(z.string()),
  createdBy: z.string().nullable(),
  updatedAt: z.string(),
  canEdit: z.boolean(),
});
export type StoryboardDto = z.infer<typeof storyboardDto>;

const reference = z.object({
  url: z.url().refine((u) => /^https?:/i.test(u), 'Link inválido'),
  label: z.string().trim().max(120).default(''),
});

/** Campos de uma cena (SPEC §18). Timecodes não são guardados: saem da soma das durações. */
export const sceneInput = z.object({
  title: text(160),
  groupLabel: text(80),
  durationMs: z.number().int().min(100, 'Mínimo 0,1 s').max(600_000, 'Máximo 10 min').default(3000),
  frameFileId: optionalId,
  videoFileId: optionalId,
  description: text(4000),
  action: text(4000),
  motion: text(4000),
  transition: text(200),
  audio: text(4000),
  onScreenText: text(2000),
  notes: text(4000),
  references: z.array(reference).max(20).default([]),
});
export const scenePatch = patchOf(sceneInput);
export type SceneInput = z.input<typeof sceneInput>;

export const sceneDto = z.object({
  id: z.uuid(),
  rank: z.string(),
  commentCount: z.number(),
  title: z.string(),
  groupLabel: z.string(),
  durationMs: z.number(),
  frameFileId: z.uuid().nullable(),
  videoFileId: z.uuid().nullable(),
  description: z.string(),
  action: z.string(),
  motion: z.string(),
  transition: z.string(),
  audio: z.string(),
  onScreenText: z.string(),
  notes: z.string(),
  references: z.array(z.object({ url: z.string(), label: z.string() })),
});
export type SceneDto = z.infer<typeof sceneDto>;

export const storyboardDetailDto = z.object({
  storyboard: storyboardDto,
  scenes: z.array(sceneDto),
  /** Quadros e vídeos das cenas que a pessoa pode ver. */
  files: z.array(fileDto),
});
export type StoryboardDetailDto = z.infer<typeof storyboardDetailDto>;

export const sceneMoveInput = z.object({
  beforeId: z.uuid().nullable().optional(),
  afterId: z.uuid().nullable().optional(),
});

export const storyboardVersionDto = z.object({
  id: z.uuid(),
  versionNumber: z.number(),
  label: z.string(),
  sceneCount: z.number(),
  durationMs: z.number(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type StoryboardVersionDto = z.infer<typeof storyboardVersionDto>;

/** "00:04" ou "00:04.32" (minutos:segundos.centésimos). */
export function formatTimecode(ms: number): string {
  const total = Math.max(0, Math.round(ms / 10)) / 100;
  const m = Math.floor(total / 60);
  const s = total - m * 60;
  const whole = Math.floor(s);
  const cents = Math.round((s - whole) * 100);
  const base = `${String(m).padStart(2, '0')}:${String(whole).padStart(2, '0')}`;
  return cents ? `${base}.${String(cents).padStart(2, '0')}` : base;
}
